import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtCurrency, fmtRelative } from "@/lib/format";
import { num, rows, type LabelValue } from "@/lib/kpi";
import {
  BarList,
  ErrorState,
  GapList,
  KpiCard,
  LoadingState,
  PageHeader,
  Panel,
  Stat,
  StatusPill,
} from "@/components/dash/ui";

export default function SubscriptionsPage() {
  const { range } = useFilters();
  const q = useQuery({ queryKey: ["src", "payments", range], queryFn: () => dashApi.source("payments", range) });
  const k = q.data?.kpis;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscriptions"
        description="IronPayments recurring revenue and billing health."
        right={q.data && <span className="flex items-center gap-2"><StatusPill status={q.data.status} /><span className="text-xs text-slate-500">{fmtRelative(q.data.captured_at)}</span></span>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <KpiCard label="MRR" value={num(k, "mrr")} unit="EUR" />
            <KpiCard label="ARR" value={num(k, "arr")} unit="EUR" />
            <KpiCard label="Active subs" value={num(k, "active_subscriptions")} unit="count" />
            <KpiCard label="Pending cancel" value={num(k, "pending_cancel")} unit="count" />
            <KpiCard label="New subs" value={num(k, "new_subscriptions")} unit="count" />
            <KpiCard label="Cancelled" value={num(k, "cancelled_subscriptions")} unit="count" tone="danger" />
            <KpiCard label="Open invoices" value={num(k, "open_invoices")} unit="count" tone="danger" />
            <KpiCard label="Failed (24h)" value={num(k, "failed_payments_24h")} unit="count" tone="danger" />
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Panel title="Plan split (active subs)">
              <BarList rows={rows<LabelValue>(k, "plan_split").map((r) => ({ label: (r as unknown as { name: string }).name, value: r.value }))} unit="count" />
            </Panel>
            <Panel title="Billing snapshot">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Open invoice total" value={fmtCurrency(num(k, "open_invoices_total"))} />
                <Stat label="Payments received" value={fmtCurrency(num(k, "succeeded_total"))} />
                <Stat label="Dunning active" value={num(k, "dunning_active") ?? 0} />
                <Stat label="Failed (range)" value={num(k, "failed_payments") ?? 0} />
              </div>
            </Panel>
          </div>
          {q.data && <GapList gaps={q.data.gaps} />}
        </>
      )}
    </div>
  );
}
